import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comicList, categories, categoryMap } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import type { ComicData } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const filtered = useMemo(() => {
    let result = slug === 'all' ? [...comicList] : comicList.filter((c) => c.category === slug);
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }
    return result;
  }, [slug, sortBy, statusFilter]);

  const currentCategoryName = categoryMap[slug] || '全部';

  return (
    <main className="min-h-screen pt-16">
      <SEO
        title={`${slug === 'all' ? '全部韩漫' : `${currentCategoryName}韩漫`} - 最新高清免费韩国漫画大全_热门韩漫在线阅读`}
        description={`韩漫画网${slug === 'all' ? '全部' : currentCategoryName}分类提供漫画免费韩漫服务，最新高清免费韩国漫画大全，热门韩漫支持在线阅读，满足追更需求。`}
        keywords={`漫画免费韩漫,${currentCategoryName}韩漫,免费韩国漫画大全,热门韩漫,在线阅读`}
        canonicalPath={`/category/${slug}`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategoryName}韩漫 - 韩漫画网`,
          description: `韩漫画网${currentCategoryName}分类 - 免费韩国漫画大全`,
          url: `https://hanmanhua.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫画网',
            url: 'https://hanmanhua.com',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://hanmanhua.com/category/all' },
              { '@type': 'ListItem', position: 3, name: currentCategoryName },
            ],
          },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '漫画分类', href: '/category/all' },
            { label: currentCategoryName },
          ]}
        />

        <h1 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mt-2">
          {slug === 'all' ? '全部韩漫 - 漫画免费韩漫大全' : `${currentCategoryName}韩漫 - 免费韩国漫画在线阅读`}
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          {slug === 'all'
            ? '浏览全部最新高清免费韩国漫画大全，发现您的下一个最爱'
            : `精选${currentCategoryName}类韩漫作品，高清免费在线阅读`}
        </p>

        {/* Category Tabs */}
        <div className="flex items-center gap-1 mt-6 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                slug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Sort & Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            {(['popular', 'newest', 'rating'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === s
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s === 'popular' ? '人气' : s === 'newest' ? '最新' : '评分'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap">状态：</span>
            {(['all', '连载中', '已完结'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  statusFilter === f
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {f === 'all' ? '全部' : f}
              </button>
            ))}
          </div>
        </div>

        {/* Comic Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5 mt-6">
          {filtered.length > 0 ? (
            filtered.map((comic: ComicData) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group block cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 漫画免费韩漫在线阅读`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-foreground-500 text-background-50'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                    <span className="text-xs text-accent-500 flex items-center gap-0.5">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-foreground-400">
              <i className="ri-inbox-line text-5xl mb-4"></i>
              <p className="text-sm">该分类暂无漫画作品</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}